import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { LoadingScreen, ProtectedRoute, PublicRoute } from '@/components/routing/RouteGuards';
import { Toaster } from 'react-hot-toast';
import Layout from './components/layout/Layout';
import ScrollToTop from './components/layout/ScrollToTop';
import HomePage from './pages/HomePage';
import { AuthPage } from './pages/AuthPage';
import { initializeAuth, useAuthStore } from './stores/useAuthStore';
import { checkIfUserBanned, handleBannedUser } from './utils/bannedUserCheck';
import { supabase } from './lib/supabase';
import { reportCampaignVisit, reportPushClick } from './utils/campaignCapture';
import BannedUserChecker from './components/BannedUserChecker';
import { PushPermissionGate } from './components/notifications/PushPermissionGate';
import { TrackingIdentity } from './components/tracking/TrackingIdentity';
import { SignupFollowUpResume } from './components/auth/SignupFollowUpResume';
import { PwaLoginHandoff } from './components/auth/PwaLoginHandoff';

// Lazy load components to reduce initial bundle size
const ContactPage = lazy(() => import('./pages/ContactPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const StatusPage = lazy(() => import('./pages/StatusPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage').then(module => ({ default: module.DashboardPage })));
const AnalyticsPage = lazy(() => import('./pages/AnalyticsPage').then(module => ({ default: module.AnalyticsPage })));
const SettingsPage = lazy(() => import('./pages/SettingsPage').then(module => ({ default: module.SettingsPage })));
const InstagramPage = lazy(() => import('./pages/InstagramPage').then(module => ({ default: module.InstagramPage })));
const ConnectedAccountPage = lazy(() => import('./pages/ConnectedAccountPageWithReferral'));
const VisitorAnalysisPage = lazy(() => import('./pages/VisitorAnalysisPage').then(module => ({ default: module.VisitorAnalysisPage })));
const AnalysisResultPage = lazy(() => import('./pages/AnalysisResultPage').then(module => ({ default: module.AnalysisResultPage })));
const PaymentHistoryPage = lazy(() => import('./pages/PaymentHistoryPage').then(module => ({ default: module.PaymentHistoryPage })));
const PaymentSuccessPage = lazy(() => import('./pages/PaymentSuccessPage').then(module => ({ default: module.PaymentSuccessPage })));
const PaymentCancelPage = lazy(() => import('./pages/PaymentCancelPage').then(module => ({ default: module.PaymentCancelPage })));
const DataDeletionSuccessPage = lazy(() => import('./pages/DataDeletionSuccessPage').then(module => ({ default: module.DataDeletionSuccessPage })));
const VipPlanPage = lazy(() => import('./pages/VipPlanPageNew').then(module => ({ default: module.VipPlanPageNew })));
const VipDashboardPage = lazy(() => import('./pages/VipDashboardPage').then(module => ({ default: module.VipDashboardPage })));
const VipPaymentSuccessPage = lazy(() => import('./pages/VipPaymentSuccessPage').then(module => ({ default: module.VipPaymentSuccessPage })));
const RefundPolicyPage = lazy(() => import('./pages/RefundPolicyPage').then(module => ({ default: module.RefundPolicyPage })));
const InfiniteLoadingPage = lazy(() => import('./pages/InfiniteLoadingPage'));
const ReferralLandingPage = lazy(() => import('./pages/ReferralLandingPage'));
// 기존 모달에서 사용하던 ReferralDashboard 컴포넌트를 페이지 래퍼로 감싸서 사용
const ReferralDashboard = lazy(() => import('./pages/ReferralDashboardPage'));
const ReferralGuidePage = lazy(() => import('./pages/ReferralGuidePage').then(module => ({ default: module.ReferralGuidePage })));
const AccountDeletionPage = lazy(() => import('./pages/AccountDeletionPage'));
const AdminReferralSettlement = lazy(() => import('./pages/AdminReferralSettlementPage'));
const SettlementBatchDetailPage = lazy(() => import('./pages/SettlementBatchDetailPage'));
const DataScrapingServicePage = lazy(() => import('./pages/DataScrapingServicePage'));
const AdminUsersPage = lazy(() => import('./pages/AdminUsersPage'));
const AdminUserManagementPage = lazy(() => import('./pages/AdminUserManagementPage'));
const AdminUserChecklistPage = lazy(() => import('./pages/AdminUserChecklistPage'));
const AdminSenderChecklistPage = lazy(() => import('./pages/AdminSenderChecklistPage'));
const AdminSenderManualPage = lazy(() => import('./pages/AdminSenderManualPage'));
const AdminBulkSendsPage = lazy(() => import('./pages/AdminBulkSendsPage'));
const AdminPurchaseJourneysPage = lazy(() => import('./pages/AdminPurchaseJourneysPage'));
const AdminWebPushPage = lazy(() => import('./pages/AdminWebPushPage'));
const AdminPersonalMessagesPage = lazy(() => import('./pages/AdminPersonalMessagesPage'));
const AdminDepositWatchPage = lazy(() => import('./pages/AdminDepositWatchPage'));
const AdminVisitorRevealsPage = lazy(() => import('./pages/AdminVisitorRevealsPage'));
const AdminCloseCirclePage = lazy(() => import('./pages/AdminCloseCirclePage'));
const AdminBlockWatchPage = lazy(() => import('./pages/AdminBlockWatchPage'));
const AdminReferralEngagementPage = lazy(() => import('./pages/AdminReferralEngagementPage'));
const AdminPostViewerPage = lazy(() => import('./pages/AdminPostViewerPage'));
const AdminAltAccountPage = lazy(() => import('./pages/AdminAltAccountPage'));
const AdminPersonaChatsPage = lazy(() => import('./pages/AdminPersonaChatsPage'));
const AdminCeoReviewPage = lazy(() => import('./pages/AdminCeoReviewPage'));
const AdminCeoReviewDetailPage = lazy(() => import('./pages/AdminCeoReviewDetailPage'));
const AdminCreditManagementPage = lazy(() => import('./pages/AdminCreditManagementPage'));
const AdminContactPage = lazy(() => import('./pages/AdminContactPage'));
const AdminRefundsPage = lazy(() => import('./pages/AdminRefundsPage'));
const AdminReviewsPage = lazy(() => import('./pages/AdminReviewsPage'));
const ReviewPage = lazy(() => import('./pages/ReviewPage'));
const AdminFinancePage = lazy(() => import('./pages/AdminFinancePage'));
const AdminCrewManagementPage = lazy(() => import('./pages/AdminCrewManagementPage'));
const AdminGlobalPerformancePage = lazy(() => import('./pages/AdminGlobalPerformancePage'));
const AdminGlobalPostsPage = lazy(() => import('./pages/AdminGlobalPostsPage'));
const AdminCrewPayoutLedgerPage = lazy(() => import('./pages/AdminCrewPayoutLedgerPage'));
const CreditChargePage = lazy(() => import('./pages/CreditChargePage'));
const CreditTermsPage = lazy(() => import('./pages/CreditTermsPage'));
const PersonaChatListPage = lazy(() => import('./pages/PersonaChatListPage'));
const PersonaChatRoomPage = lazy(() => import('./pages/PersonaChatRoomPage'));
const StoryLookupPage = lazy(() => import('./pages/StoryLookupPage'));

let authInitializationStarted = false;

const App: React.FC = () => {
    const { user } = useAuthStore();

    // 캠페인 착지 보고 — 로그인된 세션이 생기면 보관해 둔 UTM을 서버에 1회 기록 (2026-09-17)
    // 푸시 누름도 같은 때 기록 (2026-10-22)
    useEffect(() => {
        if (!user?.id) return;
        void reportCampaignVisit(supabase);
        void reportPushClick(supabase);
    }, [user?.id]);

    useEffect(() => {
        if (!authInitializationStarted) {
            authInitializationStarted = true;
            console.log('🚀 앱 초기화 시작');
            initializeAuth();
        }
    }, []);

    // 로그인한 사용자가 이미 인스타그램 연결된 경우 차단 확인
    useEffect(() => {
        const checkBannedStatus = async () => {
            if (user) {
                // 사용자의 전화번호 가져오기
                const phoneNumber = user.phone;

                // 사용자의 인스타그램 계정 확인 - saved_instagram_accounts 테이블 사용
                const { data: instagramData } = await supabase
                    .from('saved_instagram_accounts')
                    .select('username')
                    .eq('user_id', user.id)
                    .maybeSingle();

                if (instagramData?.username) {
                    // 인스타그램이 이미 연결된 사용자 - 차단 확인
                    const isBanned = await checkIfUserBanned(phoneNumber, instagramData.username);
                    if (isBanned) {
                        await handleBannedUser(user.id);
                    }
                }
            }
        };

        checkBannedStatus();
    }, [user]);

    return (
        <>
            {/* standalone(설치형 앱)에서 토스트가 상태바에 깔리지 않게 safe-area 오프셋 */}
            <Toaster position="top-center" containerStyle={{ top: 'calc(env(safe-area-inset-top, 0px) + 16px)' }} />
            <Router
                future={{
                    v7_startTransition: true,
                    v7_relativeSplatPath: true
                }}
            >
                {/* 페이지 이동 시 맨 위로 (뒤로가기는 예외) — 다니엘 지적 2026-07-30 */}
                <ScrollToTop />
                {/* 행동 분석(클래리티 녹화)에 로그인한 손님을 묶음 — 대표 7844, Readme/BehaviorTracking.md */}
                <TrackingIdentity />
                {/* 가입 직후 뒤처리가 새로고침으로 끊겼으면 다시 켜질 때 마저 함 — Readme/SignupFollowUpResume.md */}
                <SignupFollowUpResume />
                {/* 아이폰 홈 화면 앱 첫 실행 — 사파리에서 받아 둔 한 번용 코드로 로그인을 이어 받음 — Readme/PwaLoginHandoff.md */}
                <PwaLoginHandoff />
                <Routes>
                    {/* 공개 페이지들 */}
                    <Route path="/" element={
                        <Layout>
                            <HomePage />
                        </Layout>
                    } />

                    <Route path="/contact" element={
                        <Layout>
                            <Suspense fallback={<LoadingScreen message="페이지 로딩 중..." />}>
                                <ContactPage />
                            </Suspense>
                        </Layout>
                    } />

                    {/* 후기 쓰기 — 진심 후기 12크레딧 (2026-10-28 대표 터미널). 로그인 전엔 페이지가 로그인 안내를 보여 준다 */}
                    <Route path="/review" element={
                        <Layout>
                            <Suspense fallback={<LoadingScreen message="페이지 로딩 중..." />}>
                                <ReviewPage />
                            </Suspense>
                        </Layout>
                    } />

                    <Route path="/privacy" element={
                        <Layout>
                            <Suspense fallback={<LoadingScreen message="페이지 로딩 중..." />}>
                                <PrivacyPage />
                            </Suspense>
                        </Layout>
                    } />

                    <Route path="/terms" element={
                        <Layout>
                            <Suspense fallback={<LoadingScreen message="페이지 로딩 중..." />}>
                                <TermsPage />
                            </Suspense>
                        </Layout>
                    } />

                    {/* 도움말 페이지는 2026-10-27 대표 지시로 없앰 — 옛 주소는 문의하기로 보낸다 */}
                    <Route path="/help" element={<Navigate to="/contact" replace />} />

                    <Route path="/status" element={
                        <Layout>
                            <Suspense fallback={<LoadingScreen message="페이지 로딩 중..." />}>
                                <StatusPage />
                            </Suspense>
                        </Layout>
                    } />

                    <Route path="/data-scraping" element={
                        <Suspense fallback={<LoadingScreen message="페이지 로딩 중..." />}>
                            <DataScrapingServicePage />
                        </Suspense>
                    } />

                    <Route path="/refund-policy" element={
                        <Suspense fallback={<LoadingScreen message="페이지 로딩 중..." />}>
                            <RefundPolicyPage />
                        </Suspense>
                    } />

                    {/* 무한 로딩 페이지 (차단된 사용자용) */}
                    <Route path="/infinite-loading" element={
                        <Suspense fallback={<div className="min-h-screen bg-black" />}>
                            <InfiniteLoadingPage />
                        </Suspense>
                    } />

                    <Route path="/auth" element={
                        <PublicRoute>
                            <AuthPage />
                        </PublicRoute>
                    } />

                    {/* 이메일 회원가입 닫음 — 2026-11-03 대표 "이메일 회원가입하면 안 되지. 다 전화번호로 해야 돼."
                        옛 주소로 오면 전화번호 로그인 화면으로 보낸다. 화면 파일(EmailSignupPage)은 남겨 둠(되살리려면 이 줄만 되돌림). */}
                    <Route path="/email-signup" element={<Navigate to="/auth" replace />} />

                    {/* 리퍼럴 라우트 */}
                    <Route path="/ref/:code" element={
                        <Suspense fallback={<LoadingScreen message="리퍼럴 처리 중..." />}>
                            <ReferralLandingPage />
                        </Suspense>
                    } />

                    {/* 결제 관련 라우트 (보호되지 않음 - Stripe에서 리다이렉트) */}
                    <Route path="/payment/success" element={
                        <ProtectedRoute>
                            <Suspense fallback={<LoadingScreen message="페이지 로딩 중..." />}>
                                <PaymentSuccessPage />
                            </Suspense>
                        </ProtectedRoute>
                    } />

                    <Route path="/payment/vip-success" element={
                        <ProtectedRoute>
                            <Suspense fallback={<LoadingScreen message="페이지 로딩 중..." />}>
                                <VipPaymentSuccessPage />
                            </Suspense>
                        </ProtectedRoute>
                    } />

                    <Route path="/payment/cancel" element={
                        <Layout>
                            <Suspense fallback={<LoadingScreen message="페이지 로딩 중..." />}>
                                <PaymentCancelPage />
                            </Suspense>
                        </Layout>
                    } />

                    <Route path="/data-deletion/success" element={
                        <ProtectedRoute>
                            <Suspense fallback={<LoadingScreen message="페이지 로딩 중..." />}>
                                <DataDeletionSuccessPage />
                            </Suspense>
                        </ProtectedRoute>
                    } />

                    {/* 보호된 페이지들 */}
                    <Route path="/dashboard" element={
                        <ProtectedRoute>
                            <Layout>
                                <Suspense fallback={<LoadingScreen message="페이지 로딩 중..." />}>
                                    <DashboardPage />
                                </Suspense>
                            </Layout>
                        </ProtectedRoute>
                    } />

                    <Route path="/analytics" element={
                        <ProtectedRoute>
                            <Layout>
                                <Suspense fallback={<LoadingScreen message="페이지 로딩 중..." />}>
                                    <AnalyticsPage />
                                </Suspense>
                            </Layout>
                        </ProtectedRoute>
                    } />

                    {/* 2026-11-03 대표 '이 페이지는 아예 없애 쓸모자체가 없는데' — 영어 예시·가짜 방문자뿐이던 프로필 화면을 없애고 옛 주소는 대시보드로 */}
                    <Route path="/profile" element={<Navigate to="/dashboard" replace />} />

                    <Route path="/settings" element={
                        <ProtectedRoute>
                            <Layout>
                                <Suspense fallback={<LoadingScreen message="페이지 로딩 중..." />}>
                                    <SettingsPage />
                                </Suspense>
                            </Layout>
                        </ProtectedRoute>
                    } />

                    <Route path="/instagram" element={
                        <ProtectedRoute>
                            <Layout>
                                <Suspense fallback={<LoadingScreen message="페이지 로딩 중..." />}>
                                    <InstagramPage />
                                </Suspense>
                            </Layout>
                        </ProtectedRoute>
                    } />

                    <Route path="/connected-account" element={
                        <ProtectedRoute>
                            <Layout>
                                <Suspense fallback={<LoadingScreen message="페이지 로딩 중..." />}>
                                    <ConnectedAccountPage />
                                </Suspense>
                            </Layout>
                        </ProtectedRoute>
                    } />

                    <Route path="/referral-dashboard" element={
                        <ProtectedRoute>
                            <Layout>
                                <Suspense fallback={<LoadingScreen message="페이지 로딩 중..." />}>
                                    <ReferralDashboard />
                                </Suspense>
                            </Layout>
                        </ProtectedRoute>
                    } />

                    <Route path="/referral-guide" element={
                        <ProtectedRoute>
                            <Layout>
                                <Suspense fallback={<LoadingScreen message="페이지 로딩 중..." />}>
                                    <ReferralGuidePage />
                                </Suspense>
                            </Layout>
                        </ProtectedRoute>
                    } />

                    <Route path="/account-deletion" element={
                        <ProtectedRoute>
                            <Layout>
                                <Suspense fallback={<LoadingScreen message="페이지 로딩 중..." />}>
                                    <AccountDeletionPage />
                                </Suspense>
                            </Layout>
                        </ProtectedRoute>
                    } />

                    <Route path="/admin/referral-settlement" element={
                        <ProtectedRoute>
                            <Layout>
                                <Suspense fallback={<LoadingScreen message="페이지 로딩 중..." />}>
                                    <AdminReferralSettlement />
                                </Suspense>
                            </Layout>
                        </ProtectedRoute>
                    } />

                    <Route path="/admin/referral-settlement/:batchId" element={
                        <ProtectedRoute>
                            <Layout>
                                <Suspense fallback={<LoadingScreen message="페이지 로딩 중..." />}>
                                    <SettlementBatchDetailPage />
                                </Suspense>
                            </Layout>
                        </ProtectedRoute>
                    } />

                    <Route path="/admin/users" element={
                        <ProtectedRoute>
                            <Layout>
                                <Suspense fallback={<LoadingScreen message="페이지 로딩 중..." />}>
                                    <AdminUsersPage />
                                </Suspense>
                            </Layout>
                        </ProtectedRoute>
                    } />

                    <Route path="/admin/user-management" element={
                        <ProtectedRoute>
                            <Layout>
                                <Suspense fallback={<LoadingScreen message="페이지 로딩 중..." />}>
                                    <AdminUserManagementPage />
                                </Suspense>
                            </Layout>
                        </ProtectedRoute>
                    } />

                    <Route path="/admin/user-management-checklist" element={
                        <ProtectedRoute>
                            <Layout>
                                <Suspense fallback={<LoadingScreen message="페이지 로딩 중..." />}>
                                    <AdminUserChecklistPage />
                                </Suspense>
                            </Layout>
                        </ProtectedRoute>
                    } />

                    <Route path="/admin/sender-checklist" element={
                        <ProtectedRoute>
                            <Layout>
                                <Suspense fallback={<LoadingScreen message="페이지 로딩 중..." />}>
                                    <AdminSenderChecklistPage />
                                </Suspense>
                            </Layout>
                        </ProtectedRoute>
                    } />

                    <Route path="/admin/sender-manual" element={
                        <ProtectedRoute>
                            <Layout>
                                <Suspense fallback={<LoadingScreen message="페이지 로딩 중..." />}>
                                    <AdminSenderManualPage />
                                </Suspense>
                            </Layout>
                        </ProtectedRoute>
                    } />

                    <Route path="/admin/bulk-sends" element={
                        <ProtectedRoute>
                            <Layout>
                                <Suspense fallback={<LoadingScreen message="페이지 로딩 중..." />}>
                                    <AdminBulkSendsPage />
                                </Suspense>
                            </Layout>
                        </ProtectedRoute>
                    } />

                    <Route path="/admin/purchase-journeys" element={
                        <ProtectedRoute>
                            <Layout>
                                <Suspense fallback={<LoadingScreen message="페이지 로딩 중..." />}>
                                    <AdminPurchaseJourneysPage />
                                </Suspense>
                            </Layout>
                        </ProtectedRoute>
                    } />

                    <Route path="/admin/deposit-watch" element={
                        <ProtectedRoute>
                            <Layout>
                                <Suspense fallback={<LoadingScreen message="페이지 로딩 중..." />}>
                                    <AdminDepositWatchPage />
                                </Suspense>
                            </Layout>
                        </ProtectedRoute>
                    } />

                    <Route path="/admin/visitor-reveals" element={
                        <ProtectedRoute>
                            <Layout>
                                <Suspense fallback={<LoadingScreen message="페이지 로딩 중..." />}>
                                    <AdminVisitorRevealsPage />
                                </Suspense>
                            </Layout>
                        </ProtectedRoute>
                    } />

                    <Route path="/admin/post-viewer" element={
                        <ProtectedRoute>
                            <Layout>
                                <Suspense fallback={<LoadingScreen message="페이지 로딩 중..." />}>
                                    <AdminPostViewerPage />
                                </Suspense>
                            </Layout>
                        </ProtectedRoute>
                    } />

                    <Route path="/admin/close-circle" element={
                        <ProtectedRoute>
                            <Layout>
                                <Suspense fallback={<LoadingScreen message="페이지 로딩 중..." />}>
                                    <AdminCloseCirclePage />
                                </Suspense>
                            </Layout>
                        </ProtectedRoute>
                    } />

                    {/* 2026-10-01 리퍼럴 참여 현황 — 화면 방문·참여·링크 복사·클릭·가입·결제 단계별 + 무료 참여자 현황 */}
                    <Route path="/admin/referral-engagement" element={
                        <ProtectedRoute>
                            <Layout>
                                <Suspense fallback={<LoadingScreen message="페이지 로딩 중..." />}>
                                    <AdminReferralEngagementPage />
                                </Suspense>
                            </Layout>
                        </ProtectedRoute>
                    } />

                    {/* 2026-10-01 차단·언팔 72시간 분석 — 관리자 판매·결과 현황 */}
                    <Route path="/admin/block-watch" element={
                        <ProtectedRoute>
                            <Layout>
                                <Suspense fallback={<LoadingScreen message="페이지 로딩 중..." />}>
                                    <AdminBlockWatchPage />
                                </Suspense>
                            </Layout>
                        </ProtectedRoute>
                    } />

                    <Route path="/admin/alt-account" element={
                        <ProtectedRoute>
                            <Layout>
                                <Suspense fallback={<LoadingScreen message="페이지 로딩 중..." />}>
                                    <AdminAltAccountPage />
                                </Suspense>
                            </Layout>
                        </ProtectedRoute>
                    } />

                    <Route path="/admin/persona-chats" element={
                        <ProtectedRoute>
                            <Layout>
                                <Suspense fallback={<LoadingScreen message="페이지 로딩 중..." />}>
                                    <AdminPersonaChatsPage />
                                </Suspense>
                            </Layout>
                        </ProtectedRoute>
                    } />

                    <Route path="/admin/web-push" element={
                        <ProtectedRoute>
                            <Layout>
                                <Suspense fallback={<LoadingScreen message="페이지 로딩 중..." />}>
                                    <AdminWebPushPage />
                                </Suspense>
                            </Layout>
                        </ProtectedRoute>
                    } />

                    <Route path="/admin/personal-messages" element={
                        <ProtectedRoute>
                            <Layout>
                                <Suspense fallback={<LoadingScreen message="페이지 로딩 중..." />}>
                                    <AdminPersonalMessagesPage />
                                </Suspense>
                            </Layout>
                        </ProtectedRoute>
                    } />

                    <Route path="/admin/ceo-review" element={
                        <ProtectedRoute>
                            <Layout>
                                <Suspense fallback={<LoadingScreen message="페이지 로딩 중..." />}>
                                    <AdminCeoReviewPage />
                                </Suspense>
                            </Layout>
                        </ProtectedRoute>
                    } />

                    <Route path="/admin/ceo-review/:id" element={
                        <ProtectedRoute>
                            <Layout>
                                <Suspense fallback={<LoadingScreen message="페이지 로딩 중..." />}>
                                    <AdminCeoReviewDetailPage />
                                </Suspense>
                            </Layout>
                        </ProtectedRoute>
                    } />

                    <Route path="/admin/credit-management" element={
                        <ProtectedRoute>
                            <Layout>
                                <Suspense fallback={<LoadingScreen message="페이지 로딩 중..." />}>
                                    <AdminCreditManagementPage />
                                </Suspense>
                            </Layout>
                        </ProtectedRoute>
                    } />

                    <Route path="/admin/contact" element={
                        <ProtectedRoute>
                            <Layout>
                                <Suspense fallback={<LoadingScreen message="페이지 로딩 중..." />}>
                                    <AdminContactPage />
                                </Suspense>
                            </Layout>
                        </ProtectedRoute>
                    } />

                    <Route path="/admin/refunds" element={
                        <ProtectedRoute>
                            <Layout>
                                <Suspense fallback={<LoadingScreen message="페이지 로딩 중..." />}>
                                    <AdminRefundsPage />
                                </Suspense>
                            </Layout>
                        </ProtectedRoute>
                    } />
                    <Route path="/admin/reviews" element={
                        <ProtectedRoute>
                            <Layout>
                                <Suspense fallback={<LoadingScreen message="페이지 로딩 중..." />}>
                                    <AdminReviewsPage />
                                </Suspense>
                            </Layout>
                        </ProtectedRoute>
                    } />
                    {/* 2026-10-27 매출·세무 — 부가세·소득세 신고와 연말 비용 판단 (대표 터미널) */}
                    <Route path="/admin/finance" element={
                        <ProtectedRoute>
                            <Layout>
                                <Suspense fallback={<LoadingScreen message="페이지 로딩 중..." />}>
                                    <AdminFinancePage />
                                </Suspense>
                            </Layout>
                        </ProtectedRoute>
                    } />

                    <Route path="/admin/crew-management" element={
                        <ProtectedRoute>
                            <Layout>
                                <Suspense fallback={<LoadingScreen message="페이지 로딩 중..." />}>
                                    <AdminCrewManagementPage />
                                </Suspense>
                            </Layout>
                        </ProtectedRoute>
                    } />

                    <Route path="/admin/global-performance" element={
                        <ProtectedRoute>
                            <Layout>
                                <Suspense fallback={<LoadingScreen message="성과 페이지 로딩 중..." />}>
                                    <AdminGlobalPerformancePage />
                                </Suspense>
                            </Layout>
                        </ProtectedRoute>
                    } />
                    <Route path="/admin/global-posts" element={
                        <ProtectedRoute>
                            <Layout>
                                <Suspense fallback={<LoadingScreen message="해외 글 링크 로딩 중..." />}>
                                    <AdminGlobalPostsPage />
                                </Suspense>
                            </Layout>
                        </ProtectedRoute>
                    } />

                    <Route path="/admin/crew-payout-ledger" element={
                        <ProtectedRoute>
                            <Layout>
                                <Suspense fallback={<LoadingScreen message="페이지 로딩 중..." />}>
                                    <AdminCrewPayoutLedgerPage />
                                </Suspense>
                            </Layout>
                        </ProtectedRoute>
                    } />

                    <Route path="/visitor-analysis" element={
                        <ProtectedRoute>
                            <Layout>
                                <Suspense fallback={<LoadingScreen message="페이지 로딩 중..." />}>
                                    <BannedUserChecker>
                                        <VisitorAnalysisPage />
                                    </BannedUserChecker>
                                </Suspense>
                            </Layout>
                        </ProtectedRoute>
                    } />

                    <Route path="/analysis-result/:sessionId" element={
                        <ProtectedRoute>
                            <Layout>
                                <Suspense fallback={<LoadingScreen message="페이지 로딩 중..." />}>
                                    <AnalysisResultPage />
                                </Suspense>
                            </Layout>
                        </ProtectedRoute>
                    } />

                    <Route path="/payment-history" element={
                        <ProtectedRoute>
                            <Layout>
                                <Suspense fallback={<LoadingScreen message="페이지 로딩 중..." />}>
                                    <PaymentHistoryPage />
                                </Suspense>
                            </Layout>
                        </ProtectedRoute>
                    } />

                    {/* 파트너 멤버십 유료 판매 중단 (2026-10-01 — 리퍼럴 무료 개방). 옛 링크는 리퍼럴 대시보드로 */}
                    <Route path="/partner-join" element={<Navigate to="/referral-dashboard" replace />} />

                    {/* 페르소나 챗 — 가상 캐릭터 DM (챗 화면은 자체 레이아웃이라 Layout 미사용) */}
                    <Route path="/chat" element={
                        <ProtectedRoute>
                            <Suspense fallback={<LoadingScreen message="페이지 로딩 중..." />}>
                                <PersonaChatListPage />
                            </Suspense>
                        </ProtectedRoute>
                    } />
                    <Route path="/chat/:username" element={
                        <ProtectedRoute>
                            <Suspense fallback={<LoadingScreen message="페이지 로딩 중..." />}>
                                <PersonaChatRoomPage />
                            </Suspense>
                        </ProtectedRoute>
                    } />

                    {/* 스토리 조회 페이지 (공개 계정 스토리·하이라이트 확인) */}
                    <Route path="/story-viewer" element={
                        <ProtectedRoute>
                            <Layout>
                                <Suspense fallback={<LoadingScreen message="페이지 로딩 중..." />}>
                                    <StoryLookupPage />
                                </Suspense>
                            </Layout>
                        </ProtectedRoute>
                    } />

                    {/* 크레딧 충전 페이지 */}
                    <Route path="/credit" element={
                        <ProtectedRoute>
                            <Layout>
                                <Suspense fallback={<LoadingScreen message="페이지 로딩 중..." />}>
                                    <CreditChargePage />
                                </Suspense>
                            </Layout>
                        </ProtectedRoute>
                    } />

                    {/* 크레딧 이용약관 페이지 (공개) */}
                    <Route path="/credit-terms" element={
                        <Layout>
                            <Suspense fallback={<LoadingScreen message="페이지 로딩 중..." />}>
                                <CreditTermsPage />
                            </Suspense>
                        </Layout>
                    } />

                    {/* VIP 관련 페이지 */}
                    <Route path="/vip-plan" element={
                        <ProtectedRoute>
                            <Layout>
                                <Suspense fallback={<LoadingScreen message="페이지 로딩 중..." />}>
                                    <BannedUserChecker>
                                        <VipPlanPage />
                                    </BannedUserChecker>
                                </Suspense>
                            </Layout>
                        </ProtectedRoute>
                    } />

                    <Route path="/vip-dashboard" element={
                        <ProtectedRoute>
                            <Layout>
                                <Suspense fallback={<LoadingScreen message="페이지 로딩 중..." />}>
                                    <VipDashboardPage />
                                </Suspense>
                            </Layout>
                        </ProtectedRoute>
                    } />

                    {/* 404 페이지 - 홈으로 리다이렉트 */}
                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
                {/* 푸시 허용 유도 — 전 라우트 공용 (노출 규칙은 컴포넌트 내부) */}
                <PushPermissionGate />
            </Router>
        </>
    );
};

export default App; 
